import { getBenchmarkData } from './dataProcessor';
import { ProductPriceAdvantage, ProductRankingFilters, BenchmarkFilters } from './types';

export async function calculateProductPriceAdvantages(
  filters: ProductRankingFilters
): Promise<ProductPriceAdvantage[]> {
  const advantages: ProductPriceAdvantage[] = [];

  // Fetch data for each product
  for (const product of filters.products) {
    try {
      const benchmarkFilters: BenchmarkFilters = {
        product,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        locationGroups: ['chipchip-sales-db', filters.marketGroup],
        frequency: filters.frequency,
        comparisonType: filters.comparisonType,
      };

      const data = await getBenchmarkData(benchmarkFilters);
      
      if (!data || data.locationGroups.length < 2) {
        continue;
      }

      // Find ChipChip and market group data
      const chipchipGroup = data.locationGroups.find(
        g => g.groupName.toLowerCase().includes('chipchip')
      );
      const marketGroup = data.locationGroups.find(
        g => g.groupName.toLowerCase() === filters.marketGroup.toLowerCase().replace('-', ' ')
      );

      if (!chipchipGroup || !marketGroup) {
        continue;
      }

      const metrics = calculateProductMetrics(
        product,
        filters.marketGroup,
        chipchipGroup,
        marketGroup,
        data.allDates,
        filters.comparisonType
      );

      if (filters.minDaysData && metrics.totalDays < filters.minDaysData) {
        continue;
      }

      advantages.push(metrics);
    } catch (error) {
      console.error(`Error processing product ${product}:`, error);
    }
  }

  return rankProducts(advantages, 'avgDiscountPercentage');
}

function calculateProductMetrics(
  productName: string,
  marketGroup: string,
  chipchipData: any,
  marketData: any,
  allDates: string[],
  comparisonType: 'min' | 'avg'
): ProductPriceAdvantage {
  const priceKey = comparisonType === 'min' ? 'minPrices' : 'avgPrices';
  
  let totalChipchipPrice = 0;
  let totalMarketPrice = 0;
  let totalDiscount = 0;
  let daysChipchipCheaper = 0;
  let validDays = 0;
  let maxDiscount = 0;
  let minDiscount = 0;
  const discounts: number[] = [];

  allDates.forEach((date, index) => {
    const chipchipPrice = chipchipData[priceKey][index];
    const marketPrice = marketData[priceKey][index];

    if (chipchipPrice > 0 && marketPrice > 0) {
      const discount = ((chipchipPrice - marketPrice) / marketPrice) * 100;
      
      totalChipchipPrice += chipchipPrice;
      totalMarketPrice += marketPrice;
      totalDiscount += discount;
      discounts.push(discount);
      validDays++;

      if (discount < 0) {
        daysChipchipCheaper++;
      }

      if (discount < maxDiscount || maxDiscount === 0) {
        maxDiscount = discount;
      }
      if (discount > minDiscount || minDiscount === 0) {
        minDiscount = discount;
      }
    }
  });

  const avgChipchipPrice = validDays > 0 ? totalChipchipPrice / validDays : 0;
  const avgMarketPrice = validDays > 0 ? totalMarketPrice / validDays : 0;
  const avgDiscountPercentage = validDays > 0 ? totalDiscount / validDays : 0;
  const avgAbsoluteSavings = avgMarketPrice - avgChipchipPrice;
  const percentDaysCheaper = validDays > 0 ? (daysChipchipCheaper / validDays) * 100 : 0;

  // Calculate consistency score (0-100, higher = more consistent)
  let consistencyScore = 50;
  if (discounts.length > 1) {
    const mean = discounts.reduce((a, b) => a + b, 0) / discounts.length;
    const variance = discounts.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / discounts.length;
    const stdDev = Math.sqrt(variance);
    
    // Lower standard deviation = higher consistency
    // Normalize to 0-100 scale (assuming stdDev rarely exceeds 50)
    consistencyScore = Math.max(0, Math.min(100, 100 - (stdDev * 2)));
  }

  return {
    productName,
    marketGroup,
    avgDiscountPercentage,
    maxDiscountPercentage: maxDiscount,
    minDiscountPercentage: minDiscount,
    daysChipchipCheaper,
    totalDays: validDays,
    percentDaysCheaper,
    avgChipchipPrice,
    avgMarketPrice,
    avgAbsoluteSavings,
    consistencyScore,
  };
}

export function rankProducts(
  advantages: ProductPriceAdvantage[],
  sortBy: string = 'avgDiscountPercentage'
): ProductPriceAdvantage[] {
  return [...advantages].sort((a, b) => {
    switch (sortBy) {
      case 'avgDiscountPercentage':
        return a.avgDiscountPercentage - b.avgDiscountPercentage;
      case 'maxDiscountPercentage':
        return a.maxDiscountPercentage - b.maxDiscountPercentage;
      case 'consistencyScore':
        return b.consistencyScore - a.consistencyScore;
      case 'percentDaysCheaper':
        return b.percentDaysCheaper - a.percentDaysCheaper;
      case 'avgAbsoluteSavings':
        return b.avgAbsoluteSavings - a.avgAbsoluteSavings;
      default:
        return a.avgDiscountPercentage - b.avgDiscountPercentage;
    }
  });
}
